import { css, StyleSheet } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import DayPicker from 'react-day-picker-old';
import 'react-day-picker-old/lib/style.css';
import type { CaptionElementProps, NavbarElementProps, WeekdayElementProps } from 'react-day-picker-old/types/props';
import { addAlphaToHex } from '../../../models/utils/colorUtils';
import { LoadingLogo } from '../../../web/components/LoadingLogo';
import { THEME } from '../../../web/components-v2/Theme/theme';
import '../../../web/components/DayPicker/styles.less';
import { DayPickerNavbarIcon } from '../../../web/components/svgs/icons/DayPickerNavbarIcon';
import { background, brandPrimaryHover, white } from '../../../web/styles/colors';
import { CssSelectors } from '../../../web/styles/styles';
import { EventCard } from '../../components/EventCard';
import type { IEventLandingItem } from '../../queries/useEventsLanding';
import { getEventDateKey } from '../../utils/dateKey';

interface EventsCalendarProps {
	events: IEventLandingItem[];
	month: Date;
	onMonthChange: (month: Date) => void;
	isLoading?: boolean;
}

/**
 * Custom weekday element that renders full weekday names (e.g., "Sunday", "Monday")
 * instead of abbreviated names to match content-calendar styling.
 */
const Weekday: React.FC<WeekdayElementProps> = props => {
	const { className, localeUtils, weekday, locale } = props;
	const name = localeUtils?.formatWeekdayLong(weekday, locale);
	return (
		<div className={className} title={name}>
			{name}
		</div>
	);
};

/**
 * Empty caption element - month name is rendered by NavBar instead
 */
const Caption: React.FC<CaptionElementProps> = () => {
	return null;
};

/**
 * Custom navbar element that renders month/year with navigation buttons
 * to match content-calendar styling.
 */
const NavBar: React.FC<NavbarElementProps> = props => {
	const monthMoment = props.previousMonth
		? moment(props.previousMonth).add(1, 'month')
		: moment(props.nextMonth).subtract(1, 'month');
	const monthName = props.localeUtils.getMonths(props.locale)[monthMoment.month()];

	const onPrevMonthClicked = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		props.onPreviousClick();
	};

	const onNextMonthClicked = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		props.onNextClick();
	};

	return (
		<div className={props.className}>
			<button
				onClick={onPrevMonthClicked}
				className='DayPicker-NavBar-prev-button'
				aria-label='Previous month'
				type='button'
			>
				<DayPickerNavbarIcon />
			</button>
			<span className='truncate-text DayPicker-NavBar-month'>{`${monthName} ${monthMoment.year()}`}</span>
			<button
				onClick={onNextMonthClicked}
				className='DayPicker-NavBar-next-button'
				aria-label='Next month'
				type='button'
			>
				<DayPickerNavbarIcon />
			</button>
		</div>
	);
};

/**
 * Desktop calendar view component that displays events on a traditional month grid.
 * Events are shown on their respective dates with EventCard components.
 */
export const EventsCalendar: React.FC<EventsCalendarProps> = ({ events, month, onMonthChange, isLoading }) => {
	// Group events by date for efficient lookup
	const eventsByDate = React.useMemo(() => {
		const grouped = new Map<string, IEventLandingItem[]>();
		const formatterCache = new Map<string, Intl.DateTimeFormat>();

		events.forEach(event => {
			const dateKey = getEventDateKey(event, formatterCache);
			const existing = grouped.get(dateKey) || [];
			grouped.set(dateKey, [...existing, event]);
		});
		return grouped;
	}, [events]);

	// Looks up events for a DayPicker calendar cell using browser-local date extraction.
	// This is intentionally correct: DayPicker cells are labeled by browser-local date,
	// and getEventDateKey produces keys using the event's venue timezone. Both produce
	// the same YYYY-MM-DD string for the cell label, so events appear on the calendar day
	// matching their venue date (e.g., a US/Eastern April 14 event shows on the April 14
	// cell regardless of the viewer's timezone). This is the desired UX — the EventCard
	// also displays the event's venue date, so card and cell always agree.
	const getEventsForDate = (date: Date): IEventLandingItem[] => {
		const year = date.getFullYear();
		const monthStr = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		const dateKey = `${year}-${monthStr}-${day}`;
		return eventsByDate.get(dateKey) || [];
	};

	// Custom day renderer to show events
	const renderDay = (day: Date) => {
		const dayEvents = getEventsForDate(day);
		const dayNumber = moment(day).format('D');
		const isToday = moment(day).isSame(moment(), 'day');
		const isPast = moment(day).isBefore(moment(), 'day');

		// Determine which style to apply based on date comparison
		const dayNumberStyle = isToday ? styles.dayNumberToday : isPast ? styles.dayNumberPast : styles.dayNumberFuture;

		return (
			<div className={css(styles.dayCell)}>
				<div className={css(dayNumberStyle)}>{dayNumber}</div>
				{dayEvents.length > 0 && (
					<div className={css(styles.eventsContainer)}>
						{dayEvents.map(event => (
							<div key={event.id} className={css(styles.eventCardWrapper)}>
								<EventCard event={event} compact />
							</div>
						))}
					</div>
				)}
			</div>
		);
	};

	// Days in non-current months get a disabled visual style to indicate
	// the calendar is read-only when navigating away from the current month
	const currentMonth = moment().startOf('month');
	const viewingMonth = moment(month).startOf('month');
	const isNotCurrentMonth = !viewingMonth.isSame(currentMonth, 'month');

	// If viewing a month that's not current, disable all days in that month
	const disabledDays = isNotCurrentMonth
		? [
				{
					from: viewingMonth.toDate(),
					to: moment(viewingMonth).endOf('month').toDate(),
				},
			]
		: undefined;

	return (
		<div className={css(styles.calendarContainer)}>
			{isLoading && (
				<div className={css(styles.loadingOverlay)} role='status' aria-label='Loading events'>
					<LoadingLogo size={60} />
				</div>
			)}
			<DayPicker
				month={month}
				onMonthChange={onMonthChange}
				renderDay={renderDay}
				showOutsideDays={true}
				fixedWeeks={true}
				weekdayElement={Weekday}
				captionElement={Caption}
				navbarElement={NavBar}
				className={css(styles.dayPicker)}
				disabledDays={disabledDays}
			/>
		</div>
	);
};

const styles = StyleSheet.create({
	calendarContainer: {
		maxWidth: THEME.breakpoints.xl,
		boxSizing: 'border-box',
		margin: '0 auto',
		padding: `0 ${THEME.spacing.md}px`,
		position: 'relative',
		[THEME.queries.md]: {
			padding: '0 12px',
		},
	},
	loadingOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: addAlphaToHex(white, 0.8, background),
		zIndex: 10,
	},
	dayPicker: {
		// Override default DayPicker styles to match content-calendar
		flexShrink: 0,
		margin: 'auto',
		width: '100%',
		...CssSelectors.allDescendants(
			{
				width: '100%',
			},
			'.DayPicker-wrapper'
		),
		...CssSelectors.allDescendants(
			{
				display: 'flex',
				flexDirection: 'column',
				margin: 0,
				width: '98%',
			},
			'.DayPicker-Month'
		),
		...CssSelectors.allDescendants(
			{
				display: 'flex',
				alignItems: 'center',
				minHeight: 30,
				padding: '0 17px',
				width: 350,
			},
			'.DayPicker-NavBar'
		),
		...CssSelectors.allDescendants(
			{
				flexGrow: 1,
				textAlign: 'center',
				fontSize: THEME.typography.fontSizes.xxl,
				color: THEME.colors.text.main,
				width: 'calc(100% - 40px)',
			},
			'.DayPicker-NavBar-month'
		),
		...CssSelectors.allDescendants(
			{
				padding: 5,
				width: 20,
			},
			'.DayPicker-NavBar-prev-button'
		),
		...CssSelectors.allDescendants(
			{
				padding: 5,
				width: 20,
			},
			'.DayPicker-NavBar-next-button'
		),
		...CssSelectors.allDescendants(
			{
				transform: 'rotate(180deg)',
			},
			'.DayPicker-NavBar-next-button svg'
		),
		...CssSelectors.allDescendants(
			{
				display: 'flex',
				justifyContent: 'center',
				marginTop: 20,
				width: '100%',
			},
			'.DayPicker-Weekdays'
		),
		...CssSelectors.allDescendants(
			{
				display: 'flex',
				alignItems: 'center',
				height: 30,
				width: '100%',
			},
			'.DayPicker-WeekdaysRow'
		),
		...CssSelectors.allDescendants(
			{
				display: 'flex',
				fontSize: THEME.typography.fontSizes.sm,
				justifyContent: 'center',
				width: '100%',
				color: `${THEME.colors.text.light} !important`,
				fontFamily: 'Lexend-Regular, sans-serif',
				textTransform: 'capitalize',
			},
			'.DayPicker-Weekday'
		),
		...CssSelectors.allDescendants({}, '.DayPicker-Body'),
		...CssSelectors.allDescendants(
			{
				...CssSelectors.allChildren(
					{
						background: THEME.colors.background.light,
						':first-child': {
							background: THEME.colors.background.main,
						},
						':last-child': {
							background: THEME.colors.background.main,
						},
					},
					'div'
				),
				display: 'flex',
				justifyContent: 'space-evenly',
			},
			'.DayPicker-Week'
		),
		...CssSelectors.allDescendants(
			{
				fontSize: '10px !important',
				height: '16vh',
				maxHeight: 220,
				minHeight: 175,
				outline: `1px solid ${THEME.colors.border.light}`,
				overflowX: 'hidden',
				overflowY: 'auto',
				padding: 0,
				textAlign: 'start',
				verticalAlign: 'top',
				width: '100%',
			},
			'.DayPicker-Day'
		),
		...CssSelectors.allDescendants(
			{
				background: `${addAlphaToHex(brandPrimaryHover, 0.05, white)} !important`,
			},
			'.DayPicker-Day--today'
		),
		...CssSelectors.allDescendants(
			{
				borderRadius: 0,
			},
			'.DayPicker-Day--selected'
		),
		...CssSelectors.allDescendants(
			{
				pointerEvents: 'auto',
				color: `${THEME.colors.text.light} !important`,
			},
			'.DayPicker-Day--disabled'
		),
		...CssSelectors.allDescendants(
			{
				opacity: 0.5,
			},
			'.DayPicker-Day--outside'
		),
	},
	dayCell: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		minHeight: 175,
	},
	dayNumberPast: {
		padding: '8px 10px',
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.light,
		textAlign: 'right',
		lineHeight: 1,
	},
	dayNumberToday: {
		padding: '8px 10px',
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.primary.main,
		textAlign: 'right',
		lineHeight: 1,
	},
	dayNumberFuture: {
		padding: '8px 10px',
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.main,
		textAlign: 'right',
		lineHeight: 1,
	},
	eventsContainer: {
		flex: 1,
		padding: '4px 6px 6px 6px',
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	eventCardWrapper: {
		fontSize: THEME.typography.fontSizes.xxs,
		width: '100%',
	},
});
